import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Undo as UndoIcon } from '@mui/icons-material';
import { api } from '../services/api';

interface UndoPaymentProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  payment: {
    id: number;
    amount: number;
    customer_name: string;
    customer_surname: string;
    sale_id: number;
    installment_number: number;
    payment_date: string;
  } | null;
}

interface RelatedPayments {
  count: number;
  total_amount: number;
  payments: { id: number; installment_number: number; amount: number }[];
}

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);

const UndoPayment: React.FC<UndoPaymentProps> = ({
  open,
  onClose,
  onSuccess,
  payment
}) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [related, setRelated] = useState<RelatedPayments | null>(null);
  const [loadingRelated, setLoadingRelated] = useState(false);

  useEffect(() => {
    if (open && payment?.id) {
      setLoadingRelated(true);
      api.get(`/payments/related/${payment.id}`)
        .then((res) => setRelated(res.data))
        .catch(() => setRelated(null))
        .finally(() => setLoadingRelated(false));
    } else {
      setRelated(null);
    }
  }, [open, payment?.id]);

  const handleUndoOne = async () => {
    if (!payment?.id) return;
    setLoading(true);
    setError('');
    try {
      await api.delete(`/payments/${payment.id}`, { data: { reason: reason || undefined } });
      onSuccess();
      onClose();
      setReason('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al deshacer el pago');
    } finally {
      setLoading(false);
    }
  };

  const handleUndoAll = async () => {
    if (!related || related.count <= 1) return;
    setLoading(true);
    setError('');
    try {
      const ids = related.payments.map((p) => p.id);
      await api.post('/payments/bulk-undo', { payment_ids: ids, reason: reason || undefined });
      onSuccess();
      onClose();
      setReason('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al deshacer los pagos');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  if (!payment) return null;

  const hasMultiple = related && related.count > 1;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main', pb: 1 }}>
        <UndoIcon />
        Deshacer Pago
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {payment.customer_surname} {payment.customer_name}
          </Typography>

          <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Venta:</strong> #{payment.sale_id} &nbsp;|&nbsp;
              <strong>Cuota:</strong> #{payment.installment_number} &nbsp;|&nbsp;
              <strong>Monto:</strong> {fmtCurrency(payment.amount)}
            </Typography>
          </Box>

          {loadingRelated && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Buscando pagos relacionados...</Typography>
            </Box>
          )}

          {hasMultiple && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Este pago es parte de un cobro de <strong>{related!.count} cuotas</strong> por{' '}
              <strong>{fmtCurrency(related!.total_amount)}</strong>
              {' '}(cuotas {related!.payments[0].installment_number} a {related!.payments[related!.payments.length - 1].installment_number}).
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Motivo (opcional):
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ej: Error en el cobro, pago duplicado, etc."
          variant="outlined"
          size="small"
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, flexDirection: hasMultiple ? 'column' : 'row', alignItems: 'stretch' }}>
        {hasMultiple ? (
          <>
            <Button
              onClick={handleUndoAll}
              disabled={loading}
              variant="contained"
              color="error"
              startIcon={<UndoIcon />}
              fullWidth
            >
              {loading ? 'Deshaciendo...' : `Deshacer las ${related!.count} cuotas (${fmtCurrency(related!.total_amount)})`}
            </Button>
            <Button
              onClick={handleUndoOne}
              disabled={loading}
              variant="outlined"
              color="error"
              fullWidth
            >
              Solo deshacer cuota #{payment.installment_number} ({fmtCurrency(payment.amount)})
            </Button>
            <Button onClick={handleClose} disabled={loading} color="inherit" fullWidth>
              Cancelar
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading} color="inherit">
              Cancelar
            </Button>
            <Button
              onClick={handleUndoOne}
              disabled={loading}
              variant="contained"
              color="error"
              startIcon={<UndoIcon />}
            >
              {loading ? 'Deshaciendo...' : 'Deshacer Pago'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UndoPayment;
